/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  EuiButtonGroup,
  EuiEmptyPrompt,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHealth,
  EuiIcon,
  EuiPanel,
  EuiSearchBar,
  EuiSkeletonText,
  EuiSpacer,
  Query,
  SearchFilterConfig,
} from "@inscopix/ideas-eui";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { ContainerCard } from "./ContainerCard";
import { FlyoutContainerInfo } from "./FlyoutContainerInfo";
import { useFlyoutContext } from "providers/FlyoutProvider/FlyoutProvider";
import { ContainerDjango, useAllContainersQuery } from "./ContainerBrowser.api";
import { chain, orderBy } from "lodash";
import { getTagColor } from "./Tag";
import { isNonNull } from "utils/isNonNull";
import { UserAvatar } from "components/UserAvatar/UserAvatar";
import { CallOutError } from "components/CallOutError/CallOutError";
import { PromptFirstCustomContainer } from "./PromptFirstCustomContainer";
import { VList } from "virtua";
import {
  ToggleFilter,
  ToggleFilterProps,
} from "components/ProjectsCards/ToggleFilter";
import { useIdeasSearchParams } from "hooks/useIdeasSearchParams";
import { captureException } from "@sentry/react";
import { useEuiSearchBar } from "hooks/useEuiSearchBar";

const items = [
  { name: "Date Created", value: "dateCreated", first: "desc" },
  { name: "Date Updated", value: "dateUpdated", first: "desc" },
  { name: "Name", value: "name", first: "asc" },
] as const;
interface SortFilterProps {
  selected: ToggleFilterProps<typeof items>["selected"];
  onChange: ToggleFilterProps<typeof items>["onChange"];
}

const SortFilter = ({ selected, onChange }: SortFilterProps) => {
  return <ToggleFilter items={items} selected={selected} onChange={onChange} />;
};

type Scope = "custom" | "all";

interface ScopeFilterProps {
  scope: Scope;
  setScope: (scope: Scope) => void;
}

const ScopeFilter = ({ scope, setScope }: ScopeFilterProps) => {
  return (
    <EuiButtonGroup
      legend="Scope filter"
      options={[
        {
          id: "all",
          label: "All",
          css: css`
            border-start-end-radius: 0px !important;
            border-end-end-radius: 0px !important;
          `,
        },
        { id: "custom", label: "Custom" },
      ]}
      idSelected={scope}
      onChange={(scope) => setScope(scope as Scope)}
      buttonSize="m"
    />
  );
};

const styles = {
  root: css`
    height: 100%;
    padding: 24px;
  `,
};

const QUERY_FIELDS = {
  author: "user.email",
  tags: "tags" satisfies keyof ContainerDjango,
} as const;

interface ContainerBrowserProps {
  tenantId: number;
}

export const ContainerBrowser = ({ tenantId }: ContainerBrowserProps) => {
  const { data, isLoading, error } = useAllContainersQuery();
  const { flyout, setFlyout, closeFlyout } = useFlyoutContext();
  // const [query, setQuery] = useState<Query>(Query.parse(""));

  const { query, onChange, searchBarRef } = useEuiSearchBar();
  const [scope, setScope] = useState<Scope>("all");
  const { getParam, setParam } = useIdeasSearchParams();
  const [sort, setSort] = useState<ToggleFilterProps<typeof items>["selected"]>(
    { item: "dateCreated", state: "desc" },
  );
  const isCustomContainer = data?.some(
    (container) => container.tenant?.id === tenantId,
  );
  const [selectedContainerId, setSelectedContainerId] = useState<number>();
  const selectedContainer = (data ?? []).find(
    (c) => c.id === selectedContainerId,
  );

  const containers = (() => {
    let containers = data ?? [];

    if (query) {
      containers = Query.execute(query, containers);
    }

    const filteredContainers = containers.filter((container) => {
      switch (scope) {
        case "custom":
          return container.tenant?.id === tenantId;
        default:
          return true;
      }
    });

    switch (sort.item) {
      case "dateCreated":
        return orderBy(
          filteredContainers,
          (container) => new Date(container.date_created).getTime(),
          sort.state,
        );
      case "name":
        return orderBy(
          filteredContainers,
          (container) => container.full_name.toLocaleLowerCase(),
          sort.state,
        );
      case "dateUpdated":
        return orderBy(
          filteredContainers,
          (container) => new Date(container.date_updated).getTime(),
          sort.state,
        );
    }
  })();

  const onChangeSort = useCallback(
    (selected: ToggleFilterProps<typeof items>["selected"]) => {
      setParam("SORT_FIELD", selected.item);
      setParam("SORT_ORDER", selected.state);
      setSort(selected);
    },
    [setParam],
  );

  const searchFilters: SearchFilterConfig[] = useMemo(() => {
    // Defer registering filters until data loads
    // If this is done before options are populated eui won't display the pills
    // for filters restored from the route until the dropdown is opened
    if (data === undefined) {
      return [];
    }

    return [
      {
        type: "custom_component",
        component: () => (
          <ScopeFilter
            scope={scope}
            setScope={(scope) => {
              setParam("TOOL_AND_CONTAINER_SCOPE", scope);
              setScope(scope);
            }}
          />
        ),
      },
      {
        type: "field_value_selection",
        field: QUERY_FIELDS.author,
        name: "Author",
        multiSelect: "or",
        options: chain(data)
          .map((c) => c.user)
          .filter(isNonNull)
          .uniqBy((u) => u.id)
          .map((author) => {
            return {
              value: author.email,
              name: author.full_name,
              view: <UserAvatar userId={author.id} size="s" displayName />,
            };
          })
          .value(),
      },
      {
        type: "field_value_selection",
        field: "tags" satisfies keyof ContainerDjango,
        name: "Tag",
        multiSelect: "or",
        options: chain(data)
          .flatMap((c) => c.tags)
          .filter(isNonNull)
          .uniq()
          .map((tag) => {
            return {
              value: tag,
              name: tag,
              view: <EuiHealth color={getTagColor(tag)}>{tag}</EuiHealth>,
            };
          })
          .value(),
      },
      {
        type: "custom_component",
        component: () => <SortFilter selected={sort} onChange={onChangeSort} />,
      },
    ];
  }, [data, onChangeSort, scope, setParam, sort]);

  /**
   * Load sort route params into state on mount
   */
  useEffect(() => {
    const sortField = getParam("SORT_FIELD");
    const sortOrder = getParam("SORT_ORDER");
    if (sortField !== null && sortOrder !== null) {
      const selectedItem = items.find((item) => item.value === sortField);

      if (selectedItem !== undefined) {
        setSort((prev) => {
          // prevent re render when reading params that were just set
          if (prev.item !== sortField || prev.state !== sortOrder) {
            return { item: selectedItem.value, state: sortOrder };
          }
          return prev;
        });
      } else {
        captureException("Failed to restore route params in Container browser");
      }
    }
  }, [getParam]);

  return (
    <>
      {selectedContainer !== undefined && flyout === "containerInfo" && (
        <FlyoutContainerInfo
          {...selectedContainer}
          onClose={() => {
            setSelectedContainerId(undefined);
            closeFlyout();
          }}
        />
      )}
      <EuiFlexGroup css={styles.root} direction="column" gutterSize="l">
        <EuiFlexItem grow={false}>
          <EuiSearchBar
            ref={searchBarRef}
            query={query}
            onChange={onChange}
            filters={searchFilters}
            box={{
              placeholder: "Find a container...",
              incremental: false,
            }}
          />
        </EuiFlexItem>

        <EuiFlexItem style={{ overflow: "hidden" }}>
          {(() => {
            if (isLoading) {
              return <EuiSkeletonText lines={5} />;
            } else if (error) {
              return <CallOutError />;
            } else if (scope === "custom" && !isCustomContainer) {
              return <PromptFirstCustomContainer />;
            } else if (containers.length === 0) {
              return (
                <EuiPanel hasBorder>
                  <EuiEmptyPrompt
                    icon={<EuiIcon type="indexMapping" size="xl" />}
                    title={<h2>No containers found</h2>}
                    titleSize="m"
                  />
                </EuiPanel>
              );
            } else {
              return (
                <VList style={{ height: "100%" }}>
                  {containers.map((container, idx, arr) => {
                    const isLastItem = idx === arr.length - 1;
                    return (
                      <Fragment key={container.id}>
                        <ContainerCard
                          {...container}
                          onClick={() => {
                            setSelectedContainerId(container.id);
                            setFlyout("containerInfo");
                          }}
                        />
                        {!isLastItem && <EuiSpacer />}
                      </Fragment>
                    );
                  })}
                </VList>
              );
            }
          })()}
        </EuiFlexItem>
      </EuiFlexGroup>
    </>
  );
};
